import * as React from "react"
import * as SliderPrimitive from "@radix-ui/react-slider"
import { cn } from "@/lib/utils"

// ============================================================================
// SOUND WIZARD STUDIO - SLIDER COMPONENT
// ============================================================================
// Enhanced slider component with comprehensive features and accessibility
// Version: 2.1.0 - Fully accessible, performant, and extensible
// ============================================================================

interface SliderProps extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  variant?: 'default' | 'cosmic' | 'glass' | 'minimal';
  showValue?: boolean;
  valueFormatter?: (value: number) => string;
  label?: string;
  description?: string;
}

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(({
  className,
  variant = 'default',
  showValue = false,
  valueFormatter,
  label,
  description,
  value,
  min = 0,
  max = 100,
  step = 1,
  ...props
}, ref) => {
  const currentValue = value?.[0] ?? min;
  const displayValue = valueFormatter ? valueFormatter(currentValue) : currentValue.toString();

  const baseClasses = "relative flex w-full touch-none select-none items-center";

  const trackClasses = {
    default: "bg-secondary",
    cosmic: "bg-gradient-to-r from-cyan-900/50 to-purple-900/50 ",
    glass: "bg-white/20 ",
    minimal: "bg-muted/50",
  };

  const rangeClasses = {
    default: "bg-primary",
    cosmic: "bg-gradient-to-r from-cyan-400 to-purple-500",
    glass: "bg-white/80",
    minimal: "bg-foreground",
  };

  const thumbClasses = {
    default: [
      "block h-4 w-4 rounded-full border-2 border-primary bg-background",
      "shadow-lg transition-all duration-150 ease-out",
      "hover:shadow-xl",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    ],
    cosmic: [
      "block h-4 w-4 rounded-full border-2 border-cyan-400 bg-slate-900/90",
      "shadow-lg shadow-cyan-500/50 transition-all duration-150 ease-out",
      "hover:scale-110 hover:shadow-xl hover:shadow-cyan-500/70 hover:border-cyan-300",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2",
      "before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-r before:from-cyan-400/20 before:to-purple-500/20",
    ],
    glass: [
      "block h-4 w-4 rounded-full border-2 border-white/50 bg-white/20 ",
      "shadow-lg transition-all duration-150 ease-out",
      "hover:scale-110 hover:shadow-xl hover:border-white/70",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2",
    ],
    minimal: [
      "block h-3 w-3 rounded-full border border-muted bg-background",
      "shadow-sm transition-all duration-150 ease-out",
      "hover:shadow-md",
      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
    ],
  };

  return (
    <div className="space-y-2">
      {/* Label */}
      {(label || showValue) && (
        <div className="flex items-center justify-between">
          {label && (
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              {label}
            </label>
          )}
          {showValue && (
            <span className="text-sm text-muted-foreground font-mono">
              {displayValue}
            </span>
          )}
        </div>
      )}

      {/* Slider */}
      <SliderPrimitive.Root
        ref={ref}
        className={cn(baseClasses, className)}
        value={value}
        min={min}
        max={max}
        step={step}
        {...props}
      >
        <SliderPrimitive.Track className={cn(
          "relative h-1.5 w-full grow overflow-hidden rounded-full",
          trackClasses[variant]
        )}>
          <SliderPrimitive.Range className={cn(
            "absolute h-full rounded-full",
            rangeClasses[variant]
          )} />
        </SliderPrimitive.Track>

        <SliderPrimitive.Thumb
          className={cn(
            "relative block transition-all duration-150 ease-out",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            thumbClasses[variant]
          )}
        />
      </SliderPrimitive.Root>

      {/* Description */}
      {description && (
        <p className="text-sm text-muted-foreground">
          {description}
        </p>
      )}
    </div>
  );
});

Slider.displayName = "Slider";

// ============================================================================
// RANGE SLIDER COMPONENT
// ============================================================================

interface RangeSliderProps extends Omit<SliderProps, 'value' | 'onValueChange'> {
  value?: [number, number];
  onValueChange?: (value: [number, number]) => void;
  minValue?: number;
  maxValue?: number;
}

const RangeSlider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  RangeSliderProps
>(({
  className,
  variant = 'default',
  showValue = false,
  valueFormatter,
  label,
  description,
  value = [0, 100],
  min = 0,
  max = 100,
  step = 1,
  onValueChange,
  minValue,
  maxValue,
  ...props
}, ref) => {
  const displayValue = valueFormatter
    ? `${valueFormatter(value[0])} - ${valueFormatter(value[1])}`
    : `${value[0]} - ${value[1]}`;

  const trackClasses = {
    default: "bg-secondary",
    cosmic: "bg-gradient-to-r from-cyan-900/50 to-purple-900/50 ",
    glass: "bg-white/20 ",
    minimal: "bg-muted/50",
  };

  const rangeClasses = {
    default: "bg-primary",
    cosmic: "bg-gradient-to-r from-cyan-400 to-purple-500",
    glass: "bg-white/80",
    minimal: "bg-foreground",
  };

  const thumbClasses = {
    default: "block h-4 w-4 rounded-full border-2 border-primary bg-background shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    cosmic: "block h-4 w-4 rounded-full border-2 border-cyan-400 bg-slate-900/90 shadow-lg shadow-cyan-500/50 hover:shadow-xl hover:shadow-cyan-500/70 hover:border-cyan-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2",
    glass: "block h-4 w-4 rounded-full border-2 border-white/50 bg-white/20 shadow-lg hover:shadow-xl hover:border-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2",
    minimal: "block h-3 w-3 rounded-full border border-muted bg-background shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
  };

  return (
    <div className="space-y-2">
      {/* Label */}
      {(label || showValue) && (
        <div className="flex items-center justify-between">
          {label && (
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              {label}
            </label>
          )}
          {showValue && (
            <span className="text-sm text-muted-foreground font-mono">
              {displayValue}
            </span>
          )}
        </div>
      )}

      {/* Range Slider */}
      <SliderPrimitive.Root
        ref={ref}
        className={cn("relative flex w-full touch-none select-none items-center", className)}
        value={value}
        min={min}
        max={max}
        step={step}
        onValueChange={onValueChange}
        {...props}
      >
        <SliderPrimitive.Track className={cn(
          "relative h-1.5 w-full grow overflow-hidden rounded-full",
          trackClasses[variant]
        )}>
          <SliderPrimitive.Range className={cn(
            "absolute h-full rounded-full",
            rangeClasses[variant]
          )} />
        </SliderPrimitive.Track>

        <SliderPrimitive.Thumb
          className={cn(
            "relative transition-all duration-150 ease-out",
            thumbClasses[variant]
          )}
        />

        <SliderPrimitive.Thumb
          className={cn(
            "relative transition-all duration-150 ease-out",
            thumbClasses[variant]
          )}
        />
      </SliderPrimitive.Root>

      {/* Description */}
      {description && (
        <p className="text-sm text-muted-foreground">
          {description}
        </p>
      )}
    </div>
  );
});

RangeSlider.displayName = "RangeSlider";

export { Slider, RangeSlider, type SliderProps, type RangeSliderProps }